import { logger } from '@heidi/runtime/logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type DictationFeedbackCallbacks,
  type DictationFeedbackSubmission,
} from '../../contracts/dictation-feedback.types';

export type DictationFeedbackSubmissionStatus = 'idle' | 'submitting' | 'success';

interface UseDictationFeedbackSubmissionOptions extends Pick<
  DictationFeedbackCallbacks,
  'onSubmitFeedback'
> {
  active: boolean;
  onSuccess?: (feedback: DictationFeedbackSubmission) => void;
}

/**
 * Owns the short-lived UI state around the injected submission handler.
 * Persistence and analytics stay outside this hook so SCR-7608 can supply
 * them without changing either feedback surface.
 */
export const useDictationFeedbackSubmission = ({
  active,
  onSubmitFeedback,
  onSuccess,
}: UseDictationFeedbackSubmissionOptions) => {
  const [status, setStatus] = useState<DictationFeedbackSubmissionStatus>('idle');
  const inFlightRef = useRef(false);
  const generationRef = useRef(0);

  const reset = useCallback(() => {
    generationRef.current += 1;
    inFlightRef.current = false;
    setStatus('idle');
  }, []);

  useEffect(() => {
    if (!active) reset();
  }, [active, reset]);

  const submitFeedback = useCallback(
    async (feedback: DictationFeedbackSubmission) => {
      if (!onSubmitFeedback || inFlightRef.current) return;

      const generation = generationRef.current;
      inFlightRef.current = true;
      setStatus('submitting');

      try {
        await onSubmitFeedback(feedback);
        if (generation === generationRef.current) {
          onSuccess?.(feedback);
          setStatus('success');
        }
      } catch (error) {
        logger.warn('[dictation/feedback] submission failed', {
          classification: 'submission_failed',
          errorName: error instanceof Error ? error.name : typeof error,
        });
        // SCR-7608 owns the API failure surface. Until it lands, return to the
        // actionable state so the current selection remains available to retry.
        if (generation === generationRef.current) setStatus('idle');
        throw error;
      } finally {
        if (generation === generationRef.current) inFlightRef.current = false;
      }
    },
    [onSubmitFeedback, onSuccess]
  );

  return {
    status,
    isSubmitting: status === 'submitting',
    submitFeedback: onSubmitFeedback ? submitFeedback : undefined,
    reset,
  };
};
